import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MVisionAppClient } from "./mvision-client-list";
import { naturalSort } from "../../util/sort";
import { SupportedModelType } from "../appConfig/appDeploymentInfoTypes";


export type ConfigurationTargetSliceState = {
    /** List of configuration targets (MVision app clients) available to current user,
     * or undefined if not yet initialized. */
    configurationTargetList: MVisionAppClient[] | undefined,

    /** Error received when trying to fetch configuration target list, or null.
     * TODO: Consider a more simplified error messaging state handling. */
    configurationTargetListError: string | null,

    /** Current user-selected configuration target (app client), or nothing. */
    current: MVisionAppClient | undefined,

    /** True if configuration target list has been fetched, false otherwise. */
    isConfigurationTargetListFetched: boolean,

    /** True if app is currently fetching configuration target related data, false otherwise. */
    isFetchingConfigurationTargetData: boolean,

    /** List of supported model types available to current configuration target, 
     * or undefined if not yet initialized or if the current configuration target
     * does not support any model types.
     */
    supportedModelTypes: SupportedModelType[] | undefined,
};


export const initialState: ConfigurationTargetSliceState = {
    configurationTargetList: undefined,
    configurationTargetListError: null,
    current: undefined,
    isConfigurationTargetListFetched: false,
    isFetchingConfigurationTargetData: false,
    supportedModelTypes: undefined,
};



/** Redux store slice for interacting with configuration targets that user has access to (these
 * used to be called app clients).
) */
const configurationTargetSlice = createSlice({
    name: 'configurationTarget',
    initialState,
    reducers: {
        /** Sets configuration targets available to current user to the given input.
         * @param action payload.configurationTargetList: List of configuration targets to use.
         * @param action payload.errorMessage: Optional error message.
         */
        configurationTargetListSet(state, action: PayloadAction<{ configurationTargetList: MVisionAppClient[] | null, errorMessage?: string }>) {
            const { configurationTargetList, errorMessage } = action.payload;

            if (configurationTargetList !== null) {
                state.configurationTargetList = configurationTargetList;
            }

            if (!errorMessage) {
                state.isConfigurationTargetListFetched = true;
            }

            state.configurationTargetListError = errorMessage !== undefined ? errorMessage : null;
        },

        /** Sets currently active configuration target.
         * @param action payload: userId of the configuration target to use, or undefined if
         * any current selection should be unselected.
         */
        currentConfigurationTargetSet(state, action: PayloadAction<string | undefined>) {
            const userId = action.payload;

            const newAppClient = userId === undefined ? undefined : state.configurationTargetList ? state.configurationTargetList.find(cl => cl.userId === userId) : undefined;
            state.current = newAppClient;
        },

        /** Sets supported model types for current configuration target.
         * @param action payload: List of supported model types.
         */
        supportedModelTypesSet(state, action: PayloadAction<SupportedModelType[] | undefined>) {
            state.supportedModelTypes = action.payload;
        },

        /** Signals that configuration target list should be fetched (or reloaded). */
        configurationTargetListFetched() {
            // this is an empty action and is only used for signalling in sagas
        },

        /** Mark that app has started fetching data for a configuration target. */
        fetchConfigurationTargetDataStarted(state) {
            state.isFetchingConfigurationTargetData = true;
        },

        /** Mark that app has finished fetching data for a configuration target. */
        fetchConfigurationTargetDataFinished(state) {
            state.isFetchingConfigurationTargetData = false;
        },
    },
    selectors: {
        selectConfigurationTargetList: (state): MVisionAppClient[] | undefined => state.configurationTargetList ?
            naturalSort(state.configurationTargetList, 'userName') : state.configurationTargetList,
        selectSupportedModelTypes: (state) => state.supportedModelTypes,
        selectCurrent: (state) => state.current,
        selectIsFetchingConfigurationTargetData: (state) => state.isFetchingConfigurationTargetData,
        selectIsConfigurationTargetListFetched: (state) => state.isConfigurationTargetListFetched,
    }
});

export const { configurationTargetListSet, currentConfigurationTargetSet, supportedModelTypesSet, configurationTargetListFetched, fetchConfigurationTargetDataStarted, fetchConfigurationTargetDataFinished } = configurationTargetSlice.actions;

// uncomment if these are needed locally
// const localSelectors = configurationTargetSlice.getSelectors();

export const { getInitialState, selectors: configurationTargetSelectors } = configurationTargetSlice;

export default configurationTargetSlice.reducer;
